import { isValidId } from '@myre/common-primitives';

import { ErrorHandler, inject } from '@angular/core';
import {
  Router,
  type ActivatedRouteSnapshot,
  type CanActivateFn,
  type Route,
  type RouterStateSnapshot,
  type UrlTree,
} from '@angular/router';
import { select, Store, type ActionType } from '@ngrx/store';
import { mergeMap, of, type Observable } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';

import { takeFirst, takeFirstDefined } from '../../helpers/observable.helpers';
import { getPreviousUrl } from '../../helpers/router.helpers';

import {
  BaseAuthService,
  customersSelector,
  loggedUserSelector,
} from './auth.service';
import {
  dispatcherFactory,
  RouterStateSerializer,
  type BaseRouterStoreState,
} from './dispatch.guard';

export interface AuthGuardConfig {
  redirect: {
    isAuth?: 'me' | 'login' | 'none';
    isNotAuth?: 'me' | 'login' | 'none';
  };
}

function redirect(
  baseAuthService: BaseAuthService,
  to: 'me' | 'login' | 'none',
): Observable<boolean | UrlTree> {
  switch (to) {
    case 'me': {
      return baseAuthService.navigateToMe();
    }
    case 'login': {
      return baseAuthService.navigateToLogin();
    }
  }
}

export function authGuard(config?: AuthGuardConfig): CanActivateFn {
  config = {
    ...config,
    redirect: {
      ...config?.redirect,
    },
  };
  config.redirect.isNotAuth ??= 'login';

  return (): Observable<boolean | UrlTree> | boolean => {
    const baseAuthService = inject(BaseAuthService);
    const errorHandler = inject(ErrorHandler);

    function _redirect(): Observable<UrlTree | boolean> {
      return (
        redirect(
          baseAuthService,
          baseAuthService.isAuth
            ? config.redirect.isAuth
            : config.redirect.isNotAuth,
        ) || of(true)
      );
    }

    if (typeof baseAuthService.isAuth === 'boolean') {
      return _redirect();
    }

    return baseAuthService.auth().pipe(
      catchError(err => {
        if (err.status !== 401) {
          errorHandler.handleError(err);
        }

        return _redirect();
      }),
    );
  };
}

export function customersGuard(): CanActivateFn {
  return (
    _: Route,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean | UrlTree> => {
    if (/^\/company\/\d/.exec(state.url)) {
      return true;
    }

    const baseAuthService = inject(BaseAuthService);
    const store = inject(Store);
    const router = inject(Router);

    return store.select(customersSelector).pipe(
      takeFirstDefined(),
      map(customers => {
        if (!customers.length) {
          return false;
        }

        let selectedCustomerId = baseAuthService.getSelectedCustomerId();

        if (
          !selectedCustomerId ||
          customers.every(c => c.id !== selectedCustomerId)
        ) {
          selectedCustomerId = customers[0].id;
        }

        return router.createUrlTree(['company', selectedCustomerId]);
      }),
    );
  };
}

export function customerGuard(config?: {
  dispatchOnNavigated?: (data: BaseRouterStoreState) => ActionType<any>[];
  dispatchOnSuccess?: (data: BaseRouterStoreState) => ActionType<any>[];
}): CanActivateFn {
  return (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> => {
    const baseAuthService = inject(BaseAuthService);
    const store$ = inject(Store);
    const router = inject(Router);
    const routerStateSerializer = inject(RouterStateSerializer);

    const dispatcher = config && dispatcherFactory(config, state);

    return store$.pipe(
      select(loggedUserSelector),
      withLatestFrom(store$.select(customersSelector)),
      takeFirst(([loggedUser]) => !!loggedUser),
      mergeMap(([loggedUser, customers]) => {
        const data = routerStateSerializer.serialize(state, route);

        if (!isValidId(data.params.customerId)) {
          return of(router.createUrlTree(getPreviousUrl(route)));
        }

        const customer = customers.find(c => c.id === data.params.customerId);

        if (!customer) {
          return of(false);
        }

        return baseAuthService
          .validate2faForUserCustomer(loggedUser, customer)
          .pipe(
            mergeMap(ret => {
              if (ret) {
                dispatcher?.();

                return of(true);
              }

              const no2faCustomer = customers.find(
                c => !c.twoFactorAuthentication,
              );

              if (!no2faCustomer) {
                return baseAuthService.logout();
              }

              if (router.navigated) {
                return of(false);
              }

              return of(router.createUrlTree(['company', no2faCustomer.id]));
            }),
          );
      }),
    );
  };
}
